/* eslint-disable max-lines */
import { Controller } from "stimulus";
import debounce from "../../functions/debounce";
import buildURLWithQuery from "../../functions/buildURLWithQuery";
import fetchHtml from "../../functions/fetchHtml";
import keyboardNavigate from "../../functions/dropdowns/keyboardNavigate";
import dispatchCustomEvent from "../../functions/dispatchCustomEvent.js";
import DOMPurify from "dompurify";

const optionSelector = "[role='option']:not([aria-disabled])";
const activeSelector = "[aria-selected='true']";

// Search-as-you-type multiselect. 

// `this.selection` holds what is selected. 
// The hidden inputs, ticked checkboxes and placeholder are all rendered from it.
export default class extends Controller {
  static get targets() {
    return ["input", "checkbox", "selectAllCheckbox", "results", "selected", "selectedTemplate"];
  }

  static get classes() {
    return ["selected"];
  }

  static get values() {
    return { url: String, name: String };
  }

  connect() {
    this.selection = this._readSelection();
    this.mouseDown = false;
    this.requestId = 0;
    this.keyboardNavigate = keyboardNavigate;
    this.onInputChange = debounce(this.onInputChange.bind(this), 300);

    this.close();
    this.inputTarget.setAttribute("spellcheck", "false");
    this._togglePlaceholderHighlighted();
  }

  // Selection methods ------------------------------------------------------------

  _readSelection() {
    return this.selectedTargets.map((input) => ({
      value: input.value,
      label: input.dataset.placeholderValue,
      locked: input.dataset.locked === "true"
    }));
  }

  // Dropdown options are never locked - the server renders those disabled and untargeted.
  _itemFor(option) {
    return {
      value: option.dataset.formSubmissionValue,
      label: option.dataset.placeholderValue,
      locked: false
    };
  }

  _isSelected(value) {
    return this.selection.some((item) => item.value === value);
  }

  _isLocked(value) {
    return this.selection.some((item) => item.value === value && item.locked);
  }

  _deselect(value) {
    if (this._isLocked(value)) return;

    this._updateSelection(this.selection.filter((item) => item.value !== value));
  }

  _toggleOption(option) {
    if (this._isSelected(option.dataset.formSubmissionValue)) {
      this._deselect(option.dataset.formSubmissionValue);
    } else {
      this._updateSelection([...this.selection, this._itemFor(option)]);
    }
  }

  // Select All adds and removes only what the search currently shows, so a selection made under an
  // earlier search is not cleared by a later, narrower one.
  _displayedValues() {
    return this.checkboxTargets.map((option) => option.dataset.formSubmissionValue);
  }

  _unselectedOptions() {
    return this.checkboxTargets
      .filter((option) => !this._isSelected(option.dataset.formSubmissionValue))
      .map((option) => this._itemFor(option));
  }

  _updateSelection(selection) {
    this.selection = selection;

    this._renderSelection();
    this._syncOptions();
    this._setPlaceholder();

    dispatchCustomEvent(this.inputTarget, "change");
  }

  _renderSelection() {
    this.selectedTargets.forEach((input) => input.remove());

    this.selection.forEach((item) => this.element.append(this._hiddenInputFor(item)));
  }

  _hiddenInputFor({ value, label, locked }) {
    const input = this.selectedTemplateTarget.content.firstElementChild.cloneNode(true);

    input.value = value;
    input.dataset.placeholderValue = label;
    if (locked) input.dataset.locked = "true";

    return input;
  }

  // Dropdown methods -------------------------------------------------------------

  onInputSelect() {
    this._fetchResults("");
  }

  onInputChange() {
    this._fetchResults(this.inputTarget.value.trim());
  }

  onInputBlur() {
    if (this.mouseDown) return;

    this.close();
  }

  onResultsMouseDown() {
    this.mouseDown = true;
    this.resultsTarget.addEventListener("mouseup", () => {
      this.mouseDown = false;
    }, { once: true });
  }

  onResultsClick(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    // Locked options carry aria-disabled, so optionSelector skips them.
    const option = event.target.closest(optionSelector);
    if (!option) return;

    this._activate(option);
    this.inputTarget.focus();
  }

  onEnterKeydown() {
    const option = this.selectedOption;
    if (!option) return;

    this._activate(option);
  }

  _activate(option) {
    this.inputTarget.value = "";

    if (option.dataset.optionValue === "select-all") {
      this._toggleSelectAll();
    } else {
      this._toggleOption(option);
    }
  }

  _toggleSelectAll() {
    // Locked entries are never displayed values, so a clear leaves them alone.
    const displayed = this._displayedValues();

    const selection = this._allOptionsSelected
      ? this.selection.filter((item) => !displayed.includes(item.value))
      : [...this.selection, ...this._unselectedOptions()];

    this._updateSelection(selection);
    this.inputTarget.focus();
  }

  async _fetchResults(query) {
    if (!this.urlValue) return;

    const request = ++this.requestId;

    try {
      const html = await fetchHtml(buildURLWithQuery(query, this.urlValue));

      // A slower earlier search must not overwrite a newer one.
      if (request !== this.requestId) return;

      this._replaceResults(html);
    } catch (error) {
      console.error(error);
    }
  }

  _replaceResults(html) {
    this.resultsTarget.innerHTML = DOMPurify.sanitize(html);

    this._syncOptions();
    this._moveSelectedToTop();
    this._identifyOptions();

    // Opened even when empty, so "No results found." is visible.
    this.open();
  }

  // Locked options are not targets, so keep the server's ticked-and-disabled rendering.
  _syncOptions() {
    this.checkboxTargets.forEach((option) => {
      option.querySelector("input").checked = this._isSelected(option.dataset.formSubmissionValue);
    });

    if (!this.hasSelectAllCheckboxTarget) return;

    this._selectAllCheckboxInput.checked = this._allOptionsSelected;
  }

  _moveSelectedToTop() {
    Array.from(this.resultsTarget.children)
      .filter((child) => this._isSelected(child.dataset.formSubmissionValue))
      .reverse()
      .forEach((child) => this.resultsTarget.prepend(child));

    if (!this.hasSelectAllCheckboxTarget) return;

    this.resultsTarget.prepend(this.selectAllCheckboxTarget);
  }

  _identifyOptions() {
    let id = 0;
    this.resultsTarget.querySelectorAll(`${optionSelector}:not([id])`).forEach((element) => {
      element.id = `${this.resultsTarget.id}-option-${id++}`;
    });
  }

  // Presentation methods ---------------------------------------------------------

  _setPlaceholder() {
    this.inputTarget.placeholder = this.selection.length
      ? `${this.selection[0].label}... (1 of ${this.selection.length})`
      : "Type to search...";

    this._togglePlaceholderHighlighted();
  }

  _togglePlaceholderHighlighted() {
    this.inputTarget.classList.toggle("dropdown-search-input-modified", this.selection.length > 0);
  }

  open() {
    if (this.resultsShown) return;

    this.resultsShown = true;
    this.element.setAttribute("aria-expanded", "true");
  }

  close() {
    if (!this.resultsShown) return;

    this.resultsShown = false;
    this.inputTarget.removeAttribute("aria-activedescendant");
    this.element.setAttribute("aria-expanded", "false");
  }

  get resultsShown() {
    return !this.resultsTarget.hidden;
  }

  set resultsShown(value) {
    this.resultsTarget.hidden = !value;
    this.resultsTarget.classList.toggle("dropdown-search-list-hidden", !value);
  }

  get _allOptionsSelected() {
    return this.checkboxTargets.length > 0 &&
      this.checkboxTargets.every((option) => this._isSelected(option.dataset.formSubmissionValue));
  }

  get options() {
    return Array.from(this.resultsTarget.querySelectorAll(optionSelector));
  }

  get selectedOption() {
    return this.resultsTarget.querySelector(activeSelector);
  }

  get selectedClassesOrDefault() {
    return this.hasSelectedClass ? this.selectedClasses : ["active"];
  }

  get _selectAllCheckboxInput() {
    return this.selectAllCheckboxTarget.querySelector("input");
  }
}
